import { usePasskeys } from "@/api/services/main/auth";
import { passkeySchema, TPasskeyForm } from "@/components/commons/PasskeyManager/passkeySchema";
import { Form } from "@/components/elements/Form";
import { FormField } from "@/components/elements/FormField";
import { Input } from "@/components/elements/Input";
import { Modal } from "@/components/elements/Modal";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { isPasskeyAlreadyRegistered, isPasskeyCancellation, isPasskeySupported, registerPasskey } from "@/features/auth/utils/passkey";
import { text } from "@/theme/colors";
import { showError } from "@/utils/error";
import { toast } from "@/utils/toast";
import { yupResolver } from "@hookform/resolvers/yup";
import { Stack, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { FC, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";

const PASSKEY_SETUP_PROMPTED_KEY = "passkey-setup-prompted";

/**
 * First-time passkey setup prompt on the dashboard: shown once per browser when WebAuthn is
 * supported and the user has no passkey yet (gated by a localStorage flag).
 */
export const PasskeySetupPrompt: FC = () => {
  const queryClient = useQueryClient();
  const passkeySupported = useMemo(() => isPasskeySupported(), []);
  const { data, isLoading } = usePasskeys();
  const passkeys = data?.data ?? [];

  const [isOpen, setIsOpen] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);

  const methods = useForm<TPasskeyForm>({
    defaultValues: { deviceName: "" },
    resolver: yupResolver(passkeySchema),
    mode: "all",
    reValidateMode: "onBlur",
  });
  const { control, reset } = methods;

  useEffect(() => {
    if (!passkeySupported || isLoading) return;
    if (passkeys.length > 0) return;
    if (localStorage.getItem(PASSKEY_SETUP_PROMPTED_KEY)) return;
    setIsOpen(true);
  }, [passkeySupported, isLoading, passkeys.length]);

  const dismiss = () => {
    localStorage.setItem(PASSKEY_SETUP_PROMPTED_KEY, "1");
    reset({ deviceName: "" });
    setIsOpen(false);
  };

  const handleRegister = async ({ deviceName }: TPasskeyForm) => {
    setIsRegistering(true);
    try {
      await registerPasskey(deviceName.trim() || undefined);
      toast.success("パスキーを登録しました。次回からパスキーでログインできます。");
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.PASSKEY.LIST] });
      dismiss();
    } catch (error) {
      if (isPasskeyCancellation(error)) return;
      if (isPasskeyAlreadyRegistered(error)) {
        toast.error("このデバイスには既にパスキーが登録されています。");
        return;
      }
      showError(error);
    } finally {
      setIsRegistering(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      title="パスキーの設定"
      labelOk="設定する"
      labelClose="後で"
      isLoadingOkBtn={isRegistering}
      preventCloseBackdrop
      enableCloseIcon={false}
      onOk={methods.handleSubmit(handleRegister)}
      onClose={dismiss}
    >
      <Stack gap={4} py="8px" width={360}>
        <Typography variant="body14" color={text.secondary}>
          パスキーを設定すると、次回からパスワードなしで、生体認証やデバイスのロック解除だけで安全にログインできます。
        </Typography>
        <Form methods={methods} onSubmit={handleRegister} enableBlocker={false} schema={passkeySchema}>
          <FormField control={control} name="deviceName">
            <Input size="lg" placeholder="入力してください" />
          </FormField>
        </Form>
      </Stack>
    </Modal>
  );
};
